
import React, { useCallback, useState, useEffect } from "react";
import { MediaDropzone } from "./MediaDropzone";
import { MediaProgressIndicator } from "./MediaProgressIndicator";
import { useMediaUpload } from "@/hooks/useMediaUpload";
import { usePreviewMediaUpload } from "@/hooks/usePreviewMediaUpload";
import { convertHeicFiles } from "@/utils/heicConvert";
import { showErrorToast } from "@/utils/toast";
import { Media } from "./types";

interface MediaUploaderProps {
  memorialId: string;
  storyId?: string;
  onUpload?: (media: Media[]) => void;
  onFilePreview?: (files: File[]) => void;
  previewMode?: boolean;
  previewFiles?: File[];
  multiple?: boolean;
  accept?: string;
  className?: string;
}

type UploadPhase = "idle" | "converting" | "uploading" | "processing" | "complete" | "error";

export const MediaUploader = ({
  memorialId,
  storyId,
  onUpload,
  onFilePreview,
  previewMode = false,
  previewFiles = [],
  multiple = true,
  accept = "image/*,video/*,audio/*",
  className = ""
}: MediaUploaderProps) => {
  const [uploadPhase, setUploadPhase] = useState<UploadPhase>("idle");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFileName, setCurrentFileName] = useState<string>("");
  const [currentFileNumber, setCurrentFileNumber] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  
  const uploadMutation = useMediaUpload();
  const previewUpload = usePreviewMediaUpload();

  const handleDrop = useCallback(async (rawFiles: File[]) => {
    // Convert any HEIC/HEIF photos (iPhone default format) to JPEG before
    // either previewing or uploading — browsers other than Safari can't render
    // HEIC in <img>, and Supabase storage should hold a renderable file.
    setUploadPhase("converting");
    const { files: acceptedFiles, failed } = await convertHeicFiles(rawFiles);
    if (failed.length > 0) {
      const names = failed.map(f => f.file.name).join(', ');
      showErrorToast(`We couldn't read ${names}. Try saving as JPEG and uploading again.`);
    }
    setUploadPhase("idle");
    if (acceptedFiles.length === 0) return;

    // CRITICAL: If in preview mode or no story ID, use file preview
    if (previewMode || !storyId) {
      if (onFilePreview) {
        // FIXED: Merge existing preview files with new files instead of replacing
        const newFiles = acceptedFiles.filter(newFile =>
          !previewFiles.some(existingFile =>
            existingFile.name === newFile.name && existingFile.size === newFile.size
          )
        );

        // Call onFilePreview with the complete merged list
        const allFiles = [...previewFiles, ...newFiles];
        onFilePreview(allFiles);
      }
      return;
    }

    // Only proceed with direct database upload for existing stories
    setUploadPhase("uploading");
    setUploadProgress(0);
    setTotalFiles(acceptedFiles.length);
    setCurrentFileNumber(0);
    
    const uploadedMedia: Media[] = [];
    const failedFiles: string[] = [];
    const BATCH_SIZE = 3; // Upload 3 files concurrently
    
    try {
      for (let i = 0; i < acceptedFiles.length; i += BATCH_SIZE) {
        const batch = acceptedFiles.slice(i, i + BATCH_SIZE);
        const batchStartIndex = i;

        // Upload batch in parallel
        const batchResults = await Promise.allSettled(
          batch.map(async (file, batchIdx) => {
            const fileNumber = batchStartIndex + batchIdx + 1;
            setCurrentFileName(`${file.name} (${fileNumber}/${acceptedFiles.length})`);

            return uploadMutation.mutateAsync({
              file,
              options: {
                memorialId,
                storyId
              }
            });
          })
        );
        
        // Process results
        batchResults.forEach((result, idx) => {
          if (result.status === 'fulfilled') {
            uploadedMedia.push(result.value);
          } else {
            failedFiles.push(batch[idx].name);
          }
        });
        
        // Update progress after each batch
        const filesCompleted = i + batch.length;
        setCurrentFileNumber(filesCompleted);
        setUploadProgress((filesCompleted / acceptedFiles.length) * 100);
      }
      
      setUploadPhase("complete");
      setUploadProgress(100);
      
      if (onUpload) {
        onUpload(uploadedMedia);
      }
      
      // Reset after a short delay
      setTimeout(() => {
        setUploadPhase("idle");
        setUploadProgress(0);
        setCurrentFileName("");
        setCurrentFileNumber(0);
        setTotalFiles(0);
      }, 2000);
      
    } catch (error) {
      console.error('[MediaUploader] Direct upload failed:', error);
      setUploadPhase("error");
      
      // Reset after error display
      setTimeout(() => {
        setUploadPhase("idle");
        setUploadProgress(0);
        setCurrentFileName("");
        setCurrentFileNumber(0);
        setTotalFiles(0);
      }, 3000);
    }
  }, [memorialId, storyId, previewMode, onUpload, onFilePreview, uploadMutation, previewFiles]);

  const acceptMap = {
    'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.heic', '.heif'],
    'image/heic': ['.heic'],
    'image/heif': ['.heif'],
    'video/*': ['.mp4', '.mov', '.avi'],
    'audio/*': ['.mp3', '.wav', '.m4a']
  };

  // Map our upload phases to the MediaProgressIndicator phases
  const getIndicatorPhase = (phase: UploadPhase): 'idle' | 'compressing' | 'uploading' | 'extracting-thumbnails' | 'finalizing' => {
    switch (phase) {
      case "converting":
        return "compressing";
      case "uploading":
        return "uploading";
      case "processing":
        return "extracting-thumbnails";
      case "complete":
      case "error":
        return "finalizing";
      default:
        return "idle";
    }
  };

  // Show progress indicator when uploading
  if (uploadPhase !== "idle") {
    return (
      <div className={`${className} flex items-center justify-center`}>
        <MediaProgressIndicator
          showProgress={true}
          uploadPhase={getIndicatorPhase(uploadPhase)}
          currentFile={currentFileNumber}
          totalFiles={totalFiles}
        />
      </div>
    );
  }

  return (
    <div className={className}>
      <MediaDropzone
        onDrop={handleDrop}
        multiple={multiple}
        accept={acceptMap}
        disabled={uploadMutation.isPending || previewUpload.isUploading}
        previewFiles={previewFiles}
      />
    </div>
  );
};
